/**
 * Options for formatting control status based on validation errors.
 */
export interface FormatControlStatusOptions {
  /** Error messages - can be a single string or array of strings */
  errors?: string | string[];
  /** Optional label to prefix error messages with (if not already present) */
  label?: string;
}

/**
 * Formatted control status result containing status and message.
 */
export interface FormattedControlStatus {
  /** Input status - will be 'error' if there are any errors, undefined if none */
  inputStatus?: 'error';
  /** Combined status message from all errors, comma-separated if multiple */
  statusMessage?: string;
}

/**
 * Formats validation errors into a standardized control status format.
 *
 * This utility function takes validation errors and an optional label, then:
 * - Normalizes errors to an array format
 * - Splits comma-separated error strings into individual errors
 * - Prefixes errors with the label if not already present
 * - Removes duplicates and empty values
 * - Returns a formatted status object
 *
 * @param options - Configuration object containing errors and optional label
 * @returns Formatted control status with inputStatus and statusMessage
 *
 * @example
 * ```typescript
 * // Single error without label
 * formatControlStatus({ errors: "is required" })
 * // Returns: { inputStatus: 'error', statusMessage: "is required" }
 *
 * // Multiple errors with label
 * formatControlStatus({
 *   errors: ["is required", "must be unique"],
 *   label: "Email"
 * })
 * // Returns: { inputStatus: 'error', statusMessage: "Email is required, Email must be unique" }
 *
 * // No errors
 * formatControlStatus({ errors: [] })
 * // Returns: {}
 * ```
 */
export function formatControlStatus({
  errors,
  label,
}: FormatControlStatusOptions): FormattedControlStatus {
  if (!errors) return {};

  // handle both string and array inputs
  const normalizedErrors = Array.isArray(errors) ? errors : [errors];
  if (normalizedErrors.length === 0) return {};

  const flattenedErrors = normalizedErrors.flatMap((error) =>
    error && error.includes(',')
      ? error.split(',').map((splitError) => splitError.trim())
      : [error],
  );

  // Add label prefix to errors (if label provided and not already present)
  const labeledErrors = label
    ? flattenedErrors.map((msg) => {
        // Check if the error already contains the property name to avoid duplication
        const alreadyContains = msg.toLowerCase().includes(label.toLowerCase());
        return alreadyContains ? msg : `${label} ${msg}`;
      })
    : flattenedErrors;

  // Remove duplicates and empty values
  const uniqueErrors = Array.from(new Set(labeledErrors.filter(Boolean)));

  if (uniqueErrors.length === 0) return {};

  return {
    inputStatus: 'error',
    statusMessage: uniqueErrors.join(', '),
  };
}
